import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  LineChart, 
  Target, 
  Calculator, 
  TrendingUp, 
  DollarSign, 
  Percent, 
  FileSpreadsheet, 
  Download,
  HelpCircle,
  BarChart,
  PieChart,
  Activity
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CalculatedResults {
  requiredRevenue: number;
  transactionsNeeded: number;
  proposalsNeeded: number;
  prospectsNeeded: number;
  monthlyTransactions: number;
  monthlyProposals: number;
  monthlyProspects: number;
  weeklyTransactions: number;
  weeklyProposals: number;
  weeklyProspects: number;
}

// Define an interface for our monthly chart data
interface MonthlyDataPoint {
  month: number;
  transactions: number;
  proposals: number;
  prospects: number;
  customTransactions?: number;
  customProposals?: number;
  customProspects?: number;
}

export function SalesPlanPage() {
  const { toast } = useToast();
  
  // Form data state
  const [formData, setFormData] = useState({
    profitTarget: 100000,
    grossMargin: 10,
    averageSale: 4500,
    closeRate: 35,
    proposalRate: 50
  });

  // Calculated results
  const [results, setResults] = useState<CalculatedResults | null>(null);
  
  // State to track custom monthly data points
  const [monthlyData, setMonthlyData] = useState<MonthlyDataPoint[]>([]);
  const [hasCustomValues, setHasCustomValues] = useState(false);
  
  // Drag state tracking
  const [isDragging, setIsDragging] = useState(false);
  const [activePoint, setActivePoint] = useState<{
    dataKey: string;
    dataIndex: number;
    initialValue: number;
  } | null>(null);
  const initialYRef = useRef<number | null>(null);
  
  // Reference to prevent re-entrancy in generateMonthlyData
  const generateMonthlyDataRunning = useRef<boolean>(false);
  
  // Reference to the chart container for mouse position calculations
  const chartContainerRef = useRef<HTMLDivElement>(null);
  
  // Calculate results on component mount and whenever form data changes
  useEffect(() => {
    if (formData.profitTarget && formData.grossMargin && formData.averageSale && formData.closeRate && formData.proposalRate) {
      calculateResults();
    }
  }, [formData]);
  
  // Function to generate monthly data with a ramp-up curve
  const generateMonthlyData = useCallback(() => {
    if (!results) {
      return;
    }
    
    // Check the dependency cycle manually
    if (generateMonthlyDataRunning.current) {

      return;
    }
    
    // Set a flag to prevent re-entrancy
    generateMonthlyDataRunning.current = true;
    
    const newData: MonthlyDataPoint[] = [];
    
    // If we have custom values, we need to recalculate remaining months to meet the annual target
    if (hasCustomValues) {
      
      // Get all datapoints with custom values
      const customPoints = monthlyData.filter(point => 
        point.customTransactions !== undefined || 
        point.customProposals !== undefined || 
        point.customProspects !== undefined
      );
      
      // Calculate total custom values
      const customTransactionsSum = customPoints.reduce((sum, point) => 
        sum + (point.customTransactions !== undefined ? point.customTransactions : 0), 0);
      const customProposalsSum = customPoints.reduce((sum, point) => 
        sum + (point.customProposals !== undefined ? point.customProposals : 0), 0);
      const customProspectsSum = customPoints.reduce((sum, point) => 
        sum + (point.customProspects !== undefined ? point.customProspects : 0), 0);
      
      
      // Calculate remaining totals needed (ensure we don't have negative values)
      const remainingTransactions = Math.max(0.1, results.transactionsNeeded - customTransactionsSum);
      const remainingProposals = Math.max(0.1, results.proposalsNeeded - customProposalsSum);
      const remainingProspects = Math.max(0.1, results.prospectsNeeded - customProspectsSum);
      
      
      // Count months that DON'T have custom values
      const nonCustomMonths = [];
      for (let i = 0; i < 12; i++) {
        const month = i + 1;
        const existingPoint = monthlyData.find(p => p.month === month);
        
        if (!existingPoint || 
            (existingPoint.customTransactions === undefined && 
             existingPoint.customProposals === undefined && 
             existingPoint.customProspects === undefined)) {
          nonCustomMonths.push(month);
        }
      }
      
      const nonCustomMonthCount = nonCustomMonths.length;
      
      // Process each month in order
      for (let i = 0; i < 12; i++) {
        const month = i + 1;
        const existingPoint = monthlyData.find(p => p.month === month);
        
        // If this month has custom values, use them
        if (existingPoint && 
            (existingPoint.customTransactions !== undefined || 
             existingPoint.customProposals !== undefined || 
             existingPoint.customProspects !== undefined)) {
          
          newData.push({
            month,
            transactions: existingPoint.customTransactions !== undefined ? 
              existingPoint.customTransactions : results.monthlyTransactions,
            proposals: existingPoint.customProposals !== undefined ? 
              existingPoint.customProposals : results.monthlyProposals,
            prospects: existingPoint.customProspects !== undefined ? 
              existingPoint.customProspects : results.monthlyProspects,
            customTransactions: existingPoint.customTransactions,
            customProposals: existingPoint.customProposals,
            customProspects: existingPoint.customProspects
          });
        } 
        // Otherwise distribute the remaining values among non-custom months
        else {
          // If no non-custom months, just use default values
          if (nonCustomMonthCount === 0) {
            newData.push({
              month,
              transactions: results.monthlyTransactions,
              proposals: results.monthlyProposals,
              prospects: results.monthlyProspects
            });
          } else {
            // Use curve-based distribution where later months get progressively more
            // Calculate position in the non-custom months (0 to nonCustomMonthCount-1)
            const position = nonCustomMonths.indexOf(month);
            // Create a weight that increases from start to end of the year
            const weight = 0.7 + (position / nonCustomMonthCount) * 0.6;
            
            // Calculate values for this month
            const transactions = remainingTransactions * weight / nonCustomMonthCount;
            const proposals = remainingProposals * weight / nonCustomMonthCount;
            const prospects = remainingProspects * weight / nonCustomMonthCount;
            
            newData.push({
              month,
              transactions,
              proposals,
              prospects
            });
          }
        }
      }
    } else {
      // If no custom values, generate standard ramp-up curve
      for (let i = 0; i < 12; i++) {
        const monthFactor = 0.7 + (i / 11) * 0.6; // Starts at 0.7, ends at 1.3
        newData.push({
          month: i + 1,
          transactions: results.monthlyTransactions * monthFactor,
          proposals: results.monthlyProposals * monthFactor,
          prospects: results.monthlyProspects * monthFactor
        });
      }
    }
    
    setMonthlyData(newData);
    
    // Reset the flag when done
    generateMonthlyDataRunning.current = false;
  }, [results, hasCustomValues, monthlyData]);
  
  // Generate monthly data only when results change or on initial load
  useEffect(() => {
    if (results) {
      // Add a check to prevent infinite loops
      if (monthlyData.length === 0) {
        // We need to make sure this is added to dependencies but with our protections
        // against infinite loops in place
        generateMonthlyData();
      }
    }
  }, [results, monthlyData.length, generateMonthlyData]);
  
  // Removed old handlers that are now embedded in the startDragging function
  
  // Start dragging a point - completely simplified approach
  const startDragging = useCallback((dataKey: string, dataIndex: number, initialValue: number, initialY: number) => {
    const newValue = initialValue * 1.1; // Increase by 10%
    
    // Update the data with the new value
    setMonthlyData(prevData => {
      const newData = [...prevData];
      if (dataIndex >= 0 && dataIndex < newData.length) {
        if (dataKey === 'transactions') {
          newData[dataIndex] = {
            ...newData[dataIndex],
            transactions: newValue,
            customTransactions: newValue
          };
        } else if (dataKey === 'proposals') {
          newData[dataIndex] = {
            ...newData[dataIndex],
            proposals: newValue,
            customProposals: newValue
          };
        } else if (dataKey === 'prospects') {
          newData[dataIndex] = {
            ...newData[dataIndex],
            prospects: newValue,
            customProspects: newValue
          };
        }
      }
      return newData;
    });
    
    setHasCustomValues(true);
    
    // Immediately recalculate
    setTimeout(() => {
      generateMonthlyData();
    }, 100);
    
  }, [setMonthlyData, setHasCustomValues, generateMonthlyData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // For numeric inputs
    const parsedValue = parseFloat(value);
    
    // Ensure percentages are within valid range
    if ((name === "grossMargin" || name === "closeRate" || name === "proposalRate") && parsedValue > 100) {
      setFormData(prev => ({ ...prev, [name]: 100 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: parsedValue }));
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
  };

  const calculateResults = () => {
    try {
      // Convert percentages to decimals for calculations
      const grossMarginDecimal = formData.grossMargin / 100;
      const closeRateDecimal = formData.closeRate / 100;
      const proposalRateDecimal = formData.proposalRate / 100;
      
      // Calculate revenue and sales metrics
      const requiredRevenue = formData.profitTarget / grossMarginDecimal;
      const totalSalesVolume = requiredRevenue;
      const transactionsNeeded = totalSalesVolume / formData.averageSale;
      const proposalsNeeded = transactionsNeeded / closeRateDecimal;
      const prospectsNeeded = proposalsNeeded / proposalRateDecimal;
      
      // Calculate monthly and weekly breakdowns
      const monthlyTransactions = transactionsNeeded / 12;
      const monthlyProposals = proposalsNeeded / 12;
      const monthlyProspects = prospectsNeeded / 12;
      
      const weeklyTransactions = transactionsNeeded / 52;
      const weeklyProposals = proposalsNeeded / 52;
      const weeklyProspects = prospectsNeeded / 52;
      
      setResults({
        requiredRevenue,
        transactionsNeeded,
        proposalsNeeded,
        prospectsNeeded,
        monthlyTransactions,
        monthlyProposals,
        monthlyProspects,
        weeklyTransactions,
        weeklyProposals,
        weeklyProspects
      });
    } catch (error) {
      console.error("Calculation error:", error);
      toast({
        title: "Calculation Error",
        description: "Please check your input values and try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = () => {
    if (!results) return;
    
    const planText = generatePlanText();
    const blob = new Blob([planText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Sales-Plan.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Sales plan downloaded",
      description: "Your sales plan has been downloaded as a text file.",
    });
  };

  const generatePlanText = (): string => {
    if (!results) return "";
    
    return `SALES PLAN: Annual Sales Plan

INPUTS:
- Annual Profit Target: ${formatCurrency(formData.profitTarget)}
- Gross Margin: ${formData.grossMargin}%
- Average Sale Value: ${formatCurrency(formData.averageSale)}
- Close Rate: ${formData.closeRate}%
- Proposal Request Rate: ${formData.proposalRate}%

CALCULATED REVENUE REQUIREMENTS:
- Required Annual Revenue: ${formatCurrency(results.requiredRevenue)}

ANNUAL ACTIVITY TARGETS:
- Transactions Needed: ${formatNumber(results.transactionsNeeded)}
- Proposals Needed: ${formatNumber(results.proposalsNeeded)}
- Prospects Needed: ${formatNumber(results.prospectsNeeded)}

MONTHLY TARGETS:
- Monthly Transactions: ${formatNumber(results.monthlyTransactions)}
- Monthly Proposals: ${formatNumber(results.monthlyProposals)}
- Monthly Prospects: ${formatNumber(results.monthlyProspects)}

WEEKLY TARGETS:
- Weekly Transactions: ${formatNumber(results.weeklyTransactions)}
- Weekly Proposals: ${formatNumber(results.weeklyProposals)}
- Weekly Prospects: ${formatNumber(results.weeklyProspects)}

SALES KPIs:
- Average Sale: ${formatCurrency(formData.averageSale)}
- Close Rate: ${formData.closeRate}%
- Proposal Request Rate: ${formData.proposalRate}%
`;
  };

  return (
    <div className="py-8 max-w-[1200px] mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
          Sales Plan Calculator
        </h1>
        <p className="text-muted-foreground">
          Calculate the key metrics and activities needed to achieve your profit targets
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950 dark:to-blue-950 border-cyan-200 dark:border-cyan-800">
          <CardHeader className="pb-2">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 w-10 h-10 rounded-full flex items-center justify-center mb-2">
              <Target className="text-white h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Actionable Goals</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Break down annual profit targets into clear monthly and weekly activity goals
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 border-indigo-200 dark:border-indigo-800">
          <CardHeader className="pb-2">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 w-10 h-10 rounded-full flex items-center justify-center mb-2">
              <TrendingUp className="text-white h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Automatically calculate revenue, transactions, proposals, and prospects needed
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-orange-50 dark:from-emerald-950 dark:to-orange-950 border-emerald-200 dark:border-emerald-800">
          <CardHeader className="pb-2">
            <div className="bg-gradient-to-r from-emerald-500 to-orange-600 w-10 h-10 rounded-full flex items-center justify-center mb-2">
              <Calculator className="text-white h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Interactive Planning</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Adjust your KPIs and see real-time updates to plan for success with confidence
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Sales Plan Calculator</CardTitle>
            <CardDescription>
              Input your key metrics below and we'll calculate your sales requirements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium">Key Performance Indicators</h3>
                    <p className="text-sm text-muted-foreground">Adjust your KPIs to calculate your sales plan</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="profitTarget" className="text-right">Profit Target</Label>
                        <span className="text-sm text-muted-foreground">${formData.profitTarget.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="profitTarget"
                          name="profitTarget"
                          type="number"
                          value={formData.profitTarget}
                          onChange={handleInputChange}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="grossMargin" className="text-right">Gross Margin</Label>
                        <span className="text-sm text-muted-foreground">{formData.grossMargin}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Percent className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="grossMargin"
                          name="grossMargin"
                          type="number"
                          value={formData.grossMargin}
                          onChange={handleInputChange}
                          className="flex-1"
                          min={1}
                          max={100}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="averageSale" className="text-right">Average Sale</Label>
                        <span className="text-sm text-muted-foreground">${formData.averageSale.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="averageSale"
                          name="averageSale"
                          type="number"
                          value={formData.averageSale}
                          onChange={handleInputChange}
                          className="flex-1"
                          min={1}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label htmlFor="proposalRate" className="text-right">Proposal Request Rate</Label>
                          <span className="text-sm text-muted-foreground">{formData.proposalRate}%</span>
                        </div>
                        <div className="pt-2">
                          <Slider
                            id="proposalRate"
                            value={[formData.proposalRate]}
                            min={1}
                            max={100}
                            step={1}
                            onValueChange={([value]) => setFormData(prev => ({ ...prev, proposalRate: value }))}
                            className="bg-gradient-to-r from-purple-300 to-blue-300 dark:from-purple-600 dark:to-blue-600 rounded-lg"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label htmlFor="closeRate" className="text-right">Close Rate</Label>
                          <span className="text-sm text-muted-foreground">{formData.closeRate}%</span>
                        </div>
                        <div className="pt-2">
                          <Slider
                            id="closeRate"
                            value={[formData.closeRate]}
                            min={1}
                            max={100}
                            step={1}
                            onValueChange={([value]) => setFormData(prev => ({ ...prev, closeRate: value }))}
                            className="bg-gradient-to-r from-cyan-300 to-blue-300 dark:from-cyan-600 dark:to-blue-600 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  {results && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium">Sales Plan Results</h3>
                        <p className="text-sm text-muted-foreground">Based on your KPI inputs</p>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950 dark:to-blue-950 border-cyan-200 dark:border-cyan-800">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Annual</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">{formatCurrency(results.requiredRevenue)}</div>
                            <p className="text-xs text-muted-foreground mt-1">Based on your KPI inputs</p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 border-indigo-200 dark:border-indigo-800">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Monthly</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">{formatCurrency(results.requiredRevenue / 12)}</div>
                            <p className="text-xs text-muted-foreground mt-1">{formatNumber(results.monthlyTransactions)} sales per month</p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-emerald-50 to-orange-50 dark:from-emerald-950 dark:to-orange-950 border-emerald-200 dark:border-emerald-800">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Weekly</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">{formatCurrency(results.requiredRevenue / 52)}</div>
                            <p className="text-xs text-muted-foreground mt-1">{formatNumber(results.weeklyTransactions)} sales per week</p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      <Tabs defaultValue="monthly">
                        <TabsList className="grid w-full grid-cols-4">
                          <TabsTrigger value="monthly">
                            <Activity className="w-4 h-4 mr-2" />
                            Monthly Ramp-up
                          </TabsTrigger>
                          <TabsTrigger value="comparison">
                            <BarChart className="w-4 h-4 mr-2" />
                            Activity Comparison
                          </TabsTrigger>
                          <TabsTrigger value="breakdown">
                            <PieChart className="w-4 h-4 mr-2" />
                            Detailed Breakdown
                          </TabsTrigger>
                          <TabsTrigger value="export">
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Export Plan
                          </TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="export" className="border rounded-md p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Save your sales plan for reference
                          </div>
                          <div className="mb-4">
                            <p className="text-sm">Download your complete sales plan with all calculated metrics.</p>
                          </div>
                          <Button variant="default" onClick={handleDownload} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                            <Download className="mr-2 h-4 w-4" /> Download Sales Plan
                          </Button>
                        </TabsContent>
                        
                        <TabsContent value="monthly" className="border rounded-md p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Monthly ramp-up throughout the year (click and drag points to adjust)
                          </div>
                          
                          {hasCustomValues && (
                            <div className="mb-4 flex justify-end">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  setHasCustomValues(false);
                                  generateMonthlyData();
                                }}
                              >
                                Reset to Default Curve
                              </Button>
                            </div>
                          )}
                          
                          <div ref={chartContainerRef} className={`relative ${isDragging ? 'cursor-ns-resize select-none' : ''}`}>
                            {isDragging && activePoint && (
                              <div className="absolute top-0 right-0 bg-primary text-white text-xs font-medium rounded-bl-md px-2 py-1 z-10">
                                Dragging {activePoint.dataKey}... Release to apply
                              </div>
                            )}
                            <ResponsiveContainer width="100%" height={300}>
                              <AreaChart
                                data={monthlyData}
                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                              >
                              <defs>
                                <linearGradient id="colorTransactions" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
                                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.2} />
                                </linearGradient>
                                <linearGradient id="colorProposals" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.2} />
                                </linearGradient>
                                <linearGradient id="colorProspects" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.2} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis 
                                dataKey="month" 
                                label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
                              />
                              <YAxis />
                              <RechartsTooltip 
                                formatter={(value: number) => [formatNumber(value), '']}
                                labelFormatter={(label) => `Month ${label}`}
                                cursor={{ strokeDasharray: '3 3' }}
                              />
                              
                              {/* Reversed order of Areas for the funnel visualization */}
                              <Area 
                                type="monotone" 
                                dataKey="prospects" 
                                name="Prospects" 
                                stroke="#10b981" 
                                fillOpacity={1} 
                                fill="url(#colorProspects)"
                                activeDot={{ 
                                  r: isDragging && activePoint?.dataKey === 'prospects' ? 10 : 8, 
                                  style: { cursor: 'ns-resize' },
                                  fill: '#10b981',
                                  stroke: isDragging && activePoint?.dataKey === 'prospects' ? "#fff" : "#fff",
                                  strokeWidth: isDragging && activePoint?.dataKey === 'prospects' ? 2 : 1,
                                  onMouseDown: (data: any, e: React.MouseEvent<SVGCircleElement>) => {
                                    if (!data.payload) return;
                                    
                                    const month = data.payload.month;
                                    const currentValue = data.payload.prospects;
                                    const dataIndex = monthlyData.findIndex(item => item.month === month);
                                    
                                    if (dataIndex === -1) return;
                                    
                                    // Start drag operation with direct handler now
                                    startDragging('prospects', dataIndex, currentValue, e.clientY);
                                    
                                    // Prevent event propagation
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <Area 
                                type="monotone" 
                                dataKey="proposals" 
                                name="Proposals" 
                                stroke="#8b5cf6" 
                                fillOpacity={1} 
                                fill="url(#colorProposals)"
                                activeDot={{ 
                                  r: isDragging && activePoint?.dataKey === 'proposals' ? 10 : 8,
                                  style: { cursor: 'ns-resize' },
                                  fill: '#8b5cf6',
                                  stroke: isDragging && activePoint?.dataKey === 'proposals' ? "#fff" : "#fff",
                                  strokeWidth: isDragging && activePoint?.dataKey === 'proposals' ? 2 : 1,
                                  onMouseDown: (data: any, e: React.MouseEvent<SVGCircleElement>) => {
                                    if (!data.payload) return;
                                    
                                    const month = data.payload.month;
                                    const currentValue = data.payload.proposals;
                                    const dataIndex = monthlyData.findIndex(item => item.month === month);
                                    
                                    if (dataIndex === -1) return;
                                    
                                    // Start drag operation with direct handler now
                                    startDragging('proposals', dataIndex, currentValue, e.clientY);
                                    
                                    // Prevent event propagation
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <Area 
                                type="monotone" 
                                dataKey="transactions" 
                                name="Transactions" 
                                stroke="#06b6d4" 
                                fillOpacity={1} 
                                fill="url(#colorTransactions)"
                                activeDot={{ 
                                  r: isDragging && activePoint?.dataKey === 'transactions' ? 10 : 8,
                                  style: { cursor: 'ns-resize' },
                                  fill: '#06b6d4',
                                  stroke: isDragging && activePoint?.dataKey === 'transactions' ? "#fff" : "#fff",
                                  strokeWidth: isDragging && activePoint?.dataKey === 'transactions' ? 2 : 1,
                                  onMouseDown: (data: any, e: React.MouseEvent<SVGCircleElement>) => {
                                    if (!data.payload) return;
                                    
                                    const month = data.payload.month;
                                    const currentValue = data.payload.transactions;
                                    const dataIndex = monthlyData.findIndex(item => item.month === month);
                                    
                                    if (dataIndex === -1) return;
                                    
                                    // Start drag operation with direct handler now
                                    startDragging('transactions', dataIndex, currentValue, e.clientY);
                                    
                                    // Prevent event propagation
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <Legend />
                            </AreaChart>
                          </ResponsiveContainer>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="comparison" className="border rounded-md p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Comparing annual activity requirements
                          </div>
                          <ResponsiveContainer width="100%" height={300}>
                            <RechartsBarChart
                              data={[
                                { name: 'Prospects', value: results.prospectsNeeded, fill: '#10b981' },
                                { name: 'Proposals', value: results.proposalsNeeded, fill: '#8b5cf6' },
                                { name: 'Transactions', value: results.transactionsNeeded, fill: '#06b6d4' }
                              ]}
                              margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <RechartsTooltip formatter={(value: number) => [formatNumber(value), 'Count']} />
                              <Bar dataKey="value" name="Count" />
                            </RechartsBarChart>
                          </ResponsiveContainer>
                        </TabsContent>
                        
                        <TabsContent value="breakdown" className="border rounded-md p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            The mathematics behind your sales requirements
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md flex justify-between">
                                <span className="font-medium">Required Revenue</span>
                                <span>{formatCurrency(results.requiredRevenue)}</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md flex justify-between">
                                <span className="font-medium">Transactions Needed</span>
                                <span>{formatNumber(results.transactionsNeeded)}</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md flex justify-between">
                                <span className="font-medium">Proposals Needed</span>
                                <span>{formatNumber(results.proposalsNeeded)}</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md flex justify-between">
                                <span className="font-medium">Prospects Needed</span>
                                <span>{formatNumber(results.prospectsNeeded)}</span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
                                <span className="font-medium block">Revenue</span>
                                <span className="text-sm text-muted-foreground">Profit Target ÷ Gross Margin</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
                                <span className="font-medium block">Transactions</span>
                                <span className="text-sm text-muted-foreground">Revenue ÷ Average Sale</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
                                <span className="font-medium block">Proposals</span>
                                <span className="text-sm text-muted-foreground">Transactions ÷ Close Rate</span>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
                                <span className="font-medium block">Prospects</span>
                                <span className="text-sm text-muted-foreground">Proposals ÷ Proposal Rate</span>
                              </div>
                            </div>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}